import React, { useState, useEffect, useContext } from 'react';
import css from 'styled-jsx/css';
import classnames from 'classnames';
const {
  AbortController
} = require('abortcontroller-polyfill/dist/cjs-ponyfill');
import { CardElement, injectStripe } from 'react-stripe-elements';
import { colors, fonts, dimensions } from '../common/Theme';

import format from 'date-fns/format';
import { formatter } from '@yupwego/string-fns';
import Pricer from '@yupwego/pricer';

import quotes from '../../services/api/quotes';
import subscriptions from '../../services/api/subscriptions';

import MatomoAsyncProvider from '../common/services/MatomoProvider';

import { EditIcon } from '../common/icons/icons';
import Form from '../common/forms/Form';
import { validate, strings } from '../common/forms/Validations';
import FormControl, { Label } from '../common/elements/FormControl';
import CountrySelector from '../common/elements/CountrySelector';
import List from '../common/elements/List.js';

import Button from '../common/elements/Button';
import paymentMethods from '../../services/api/payment_methods';

const styles = css`
  .subscription__form {
    display: flex;
    flex-direction: column;
    width: 100%;
  }
  .subscriber__nav {
    display: flex;
    flex-direction: row;
    width: 100%;
    justify-content: flex-end;
  }

  .subscription__info--emphasized {
    color: ${colors.ywgPinkishOrange};
    font-style: normal;
    font-weight: bold;
  }
  .payment__info {
    margin-bottom: ${dimensions.ywgFormSpacing};
  }
  :global(.StripeElement) {
    height: 20px;
    padding: 15px 12px;
    border: 2px solid ${colors.ywgVeryLightBlue};
    background-color: white;
    font-family: ${fonts.primaryFont};
    margin-bottom: ${dimensions.ywgFormSpacing};
  }

  :global(.StripeElement--focus) {
    border: 2px solid ${colors.ywgPinkishOrange};
  }

  :global(.StripeElement--invalid) {
    border-color: red;
  }

  :global(.StripeElement--webkit-autofill) {
    background-color: #fefde5 !important;
  }
`;
const countryStyles = css.resolve`
  .country {
    margin-bottom: ${dimensions.ywgFormSpacing};
  }
`;
const actionStyles = css.resolve`
  .action {
    margin-left: 10px;
    cursor: pointer;
  }
`;
const buttonStyles = css.resolve`
  button {
    max-width: 50%;
    min-width: 100px;
    align-self: flex-end;
    width: 200px;
  }
`;
const validators = () => {
  return {
    firstname: [strings.required],
    lastname: [strings.required],
    email: [strings.required],
    address: [strings.required],
    address_extra: [],
    postalcode: [strings.required],
    city: [strings.required],
    phone: [strings.required],
    card_holder: [strings.required]
  };
};
const SubscriberStep = ({
  id,
  travel,
  subscriber,
  beneficiaries,
  editTravel,
  editBeneficiaries,
  affiliateId,
  countries,
  className,
  ...props
}) => {
  const [state, setState] = useState({
    quotation: undefined,
    error: undefined
  });
  const tracking = useContext(MatomoAsyncProvider.Context);
  const quotation = state.quotation;
  const error = state.error;
  const today = new Date().toISOString().split('T')[0];
  const setError = error =>
    setState({
      ...state,
      error: error
    });
  useEffect(() => {
    const controller = new AbortController();
    const getQuote = async () => {
      try {
        const { quote: quotation, prices } = await quotes.createQuote(
          travel,
          beneficiaries,
          controller
        );
        const areaPricingItems = Object.entries(prices.zones).reduce(
          (acc, [area, areaPrices]) => {
            const areaTotalPrice = quotation.details.reduce(
              (acc, beneficiary) => {
                const areaPrice = beneficiary.promotion
                  ? Pricer.selectPriceRange(
                      beneficiary.details.age,
                      beneficiary.promotion.zones[area],
                      beneficiary.promotion.zones[area]
                    )
                  : Pricer.selectPriceRange(
                      beneficiary.details.age,
                      areaPrices,
                      areaPrices
                    );
                return acc + areaPrice.price;
              },
              0
            );
            const areaItem = {
              key: `subscriber-step-payment-method-${area.toLowerCase()}`,
              text: `Zone ${area.toUpperCase()} : ${areaTotalPrice.toFixed(
                2
              )}€/jour (${(areaTotalPrice * 7).toFixed(2)}€/semaine)`
            };
            return [...acc, areaItem];
          },
          []
        );
        setState(state => ({
          ...state,
          quotation: quotation,
          areaPricingItems
        }));
      } catch (error) {
        console.log(error);
        // ignore error for this component
      }
    };
    getQuote();
    return () => controller.abort();
  }, [beneficiaries, travel]);

  const trackGoal = (idGoal, customRevenue) => {
    try {
      tracking.trackGoal(idGoal, customRevenue);
    } catch (error) {
      console.log(`trackGoalError: ${idGoal} / ${customRevenue}`);
    }
  };

  const complete = async (subscriber, actions, evt) => {
    try {
      actions.setSubmitting(true);
      const { secret } = await paymentMethods.setupPayment(
        id,
        subscriber.email
      );

      const token = await props.stripe.handleCardSetup(secret);
      if (token.error) {
        console.log(token.error);
        setError({
          msg:
            `Notre banque nous informe que la validation de votre moyen de paiement a échoué avec lʼerreur suivante: ${token.error.message}.` +
            'Vous pouvez réessayer ou changer de moyen de paiement. Dans tous les cas YUPWEGO nʼeffectue aucun débit tant que vous nʼaurez pas confirmé votre souscription à lʼétape suivante.',
          code: token.error.code,
          id: id
        });
        actions.setSubmitting(false);
      } else {
        if (token && token.setupIntent && token.setupIntent.payment_method) {
          token.id = token.setupIntent.payment_method;
          const response = await subscriptions.createSubscription(
            travel,
            beneficiaries,
            subscriber,
            token,
            format(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"),
            affiliateId
          );
          if (response.ok) {
            trackGoal(1, quotation.total);
            props.onDone(subscriber, response.id, evt);
          } else {
            setError({
              msg:
                'Lʼenregistrement de votre souscription a échoué, réésayez dans quelques instants.',
              code: response.code,
              id: id
            });
            actions.setSubmitting(false);
          }
        }
      }
    } catch (error) {
      setError({
        msg:
          'Lʼenregistrement de votre souscription a échoué, réésayez dans quelques instants.',
        code: error,
        id: id
      });
      actions.setSubmitting(false);
    }
  };

  return (
    <div className={className}>
      {error ? (
        <p>
          {error.msg}. Si l&apos;erreur persiste, contactez-nous
          <a
            href={`mailto:help@yupwego.com?subject=Erreur%20lors%20de%20la%20souscription&body=Bonjour,%20je%20n%27ai%20pas%20pu%20m%27inscrire.%20Code%20erreur%20${error.code}%20pour%20la%20souscription%20${error.id}`}
          >
            📧 help@yupwego.com
          </a>
        </p>
      ) : null}
      <h2>Le souscripteur</h2>
      <Form
        initialValues={subscriber}
        validate={value => validate(value, validators())}
        onSubmit={complete}
      >
        {({ values, metas, onChange, onBlur, doSubmit, submitting }) => (
          <form onSubmit={doSubmit} className="subscription__form">
            <FormControl
              name="firstname"
              label="PRÉNOM"
              autoComplete="given-name"
              type="text"
              value={values.firstname}
              onChange={onChange}
              onBlur={onBlur}
              valid={metas.firstname.valid}
              required
            />
            <FormControl
              name="lastname"
              autoComplete="family-name"
              label="NOM DE FAMILLE"
              type="text"
              value={values.lastname}
              required
              onChange={onChange}
              onBlur={onBlur}
              error={metas.lastname.error}
              valid={metas.lastname.valid}
            />
            <FormControl
              name="email"
              autoComplete="email"
              label="EMAIL"
              type="mail"
              value={values.email}
              required
              onChange={onChange}
              onBlur={onBlur}
              error={metas.email.error}
              valid={metas.email.valid}
              autoFocus={!values.email}
            />
            <FormControl
              type="text"
              label="ADRESSE"
              name="address"
              autoComplete="address-line1"
              value={values.address}
              onChange={onChange}
              onBlur={onBlur}
              error={metas.address.error}
              valid={metas.address.valid}
              required
              tabIndex="0"
              autoFocus={!!values.email}
            />
            <FormControl
              label="COMPLÉMENT D'ADRESSE"
              type="text"
              name="address_extra"
              autoComplete="address-line2"
              value={values.address_extra}
              error={metas.address_extra.error}
              valid={metas.address_extra.valid}
              onChange={onChange}
              onBlur={onBlur}
              tabIndex="0"
            />
            <FormControl
              label="CODE POSTAL"
              type="number"
              name="postalcode"
              autoComplete="postal-code"
              value={values.postalcode}
              error={metas.postalcode.error}
              valid={metas.postalcode.valid}
              onChange={onChange}
              onBlur={onBlur}
              required
              tabIndex="0"
            />
            <FormControl
              label="VILLE"
              type="text"
              name="city"
              autoComplete="address-level2"
              value={values.city}
              onChange={onChange}
              onBlur={onBlur}
              error={metas.city.error}
              valid={metas.city.valid}
              required
              tabIndex="0"
            />
            <CountrySelector
              label="PAYS"
              name="country"
              className={classnames('country', countryStyles.className)}
              autoComplete="country-name"
              value={values.country}
              onChange={onChange}
              onBlur={onBlur}
              error={metas.country.error}
              valid={metas.country.valid}
              required
              tabIndex="0"
              countries={countries}
            />
            <FormControl
              label="TÉLÉPHONE DE CONTACT"
              type="tel"
              name="phone"
              autoComplete="phone"
              value={values.phone}
              onChange={onChange}
              onBlur={onBlur}
              error={metas.phone.error}
              valid={metas.phone.valid}
              tabIndex="0"
              required
            />
            <h2>
              Voyage{' '}
              <EditIcon
                title={'modifier le voyage'}
                className={classnames('action', actionStyles.className)}
                onClick={() => editTravel(subscriber)}
              />
            </h2>
            <List
              items={[
                {
                  key: 'subscriber-step-travel-start',
                  text: `Date du départ : ${formatter(travel.startDate)}`
                },
                {
                  key: 'subscriber-step-travel-end',
                  text: `Date de retour : ${formatter(travel.endDate) ||
                    'illimité'}`
                },
                {
                  key: 'subscriber-step-travel-destination',
                  text: `Premier pays de destination : ${travel.destination}`
                }
              ]}
            ></List>
            <h2>
              Voyageur(s){' '}
              <EditIcon
                title={'changer les voyageurs'}
                className={classnames('action', actionStyles.className)}
                onClick={() => editBeneficiaries(subscriber)}
              />
            </h2>
            <List
              items={beneficiaries.map(beneficiary => {
                return {
                  key: `subscriber-${beneficiary.key}`,
                  text: `${beneficiary.firstname} ${
                    beneficiary.lastname
                  }, ${formatter(beneficiary.birthday)}`
                };
              })}
            ></List>
            {quotation ? (
              <>
                <h2>Votre abonnement</h2>
                <p>
                  Abonnement au tarif{' '}
                  <em className="subscription__info--emphasized">
                    zone {quotation.zone.toUpperCase()}
                  </em>{' '}
                  ({travel.destination}), premier paiement de{' '}
                  <em className="subscription__info--emphasized">
                    {quotation.total.toFixed(2)} €
                  </em>{' '}
                  le{' '}
                  <em className="subscription__info--emphasized">
                    {formatter(quotation.pricingPeriodStart)}
                  </em>
                  .
                </p>
                <p>
                  Ensuite le prix de l’abonnement sera fixé en fonction du
                  dernier pays connu selon la grille suivante:
                </p>
                <List items={state.areaPricingItems || []}></List>
                <h2>Ajouter un moyen de paiement</h2>
                <p>
                  Nous allons demander à votre banque l’autorisation de prélever
                  le montant de votre abonnement hebdomadaire.
                </p>
                {today >= quotation.pricingPeriodStart ? (
                  <p className="payment__info">
                    Le premier paiement aura lieu après votre confirmation à
                    l’étape suivante.
                  </p>
                ) : (
                  <p className="payment__info">
                    Si vous changez d’avis avant le{' '}
                    {formatter(quotation.pricingPeriodStart)}, vous pourrez
                    annuler votre abonnement sans frais depuis votre espace
                    client.
                  </p>
                )}

                <Label>Carte bancaire</Label>
                <CardElement />
                <FormControl
                  name="card_holder"
                  autoComplete="name"
                  label="NOM DU PORTEUR"
                  type="text"
                  value={values.card_holder}
                  required
                  onChange={onChange}
                  onBlur={onBlur}
                  error={metas.card_holder.error}
                  valid={metas.card_holder.valid}
                />
                <div className="subscriber__nav">
                  <Button
                    className={buttonStyles.className}
                    type="submit"
                    arrow="right"
                    submitting={submitting}
                  >
                    Confirmation
                  </Button>
                </div>
                {error ? (
                  <>
                    <p>{error.msg}</p>
                    <p>
                      {' '}
                      Si l&apos;erreur persiste, contactez-nous
                      <a
                        href={`mailto:help@yupwego.com?subject=Erreur%20lors%20de%20la%20souscription&body=Bonjour,%20je%20n%27ai%20pas%20pu%20m%27inscrire.%20Code%20erreur%20${error.code}%20pour%20la%20souscription%20${error.id}`}
                      >
                        📧 help@yupwego.com
                      </a>
                    </p>
                  </>
                ) : null}
              </>
            ) : (
              <h2>Calcul du tarif final en cours...</h2>
            )}
            <style jsx>{styles}</style>
            {buttonStyles.styles}
            {countryStyles.styles}
            {actionStyles.styles}
          </form>
        )}
      </Form>
    </div>
  );
};
export default injectStripe(SubscriberStep);
